/**
 * Farrowing crate: a narrow central sow lane with protected creep bays either side.
 *
 * The central 0.65 m lane is intentionally not hidden behind a solid panel. The
 * repeated, close-set rails make the width readable in silhouette; widening it for
 * comfort would erase the single piece of information this object carries.
 */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

const PIPE_RADIUS = 0.025;
const CAGE_HALF_WIDTH = 0.325;
const BAY_HALF_WIDTH = 0.9;
const CRATE_LENGTH = 2.1;

/**
 * The crate dimensions collision has to share with the kit.
 *
 * The pen is dressed from this asset while Beat 1 adds only the colliders it
 * needs. Exporting the outboard end here prevents the drawn rail and the
 * containment wall from becoming two independently remembered measurements.
 */
export const FARROWING_CRATE_LAYOUT = {
  halfLength: CRATE_LENGTH / 2,
  outerEndRailTop: 1,
  /** Existing next-post line used as the gate-bay's intentional doorway end. */
  gateAccessLaneRailFrom: -0.5,
} as const;

/** Transform the access crate's local rail end into a mirrored hall placement. */
export function farrowingGateAccessRailEnd(crateX: number, side: -1 | 1): number {
  return crateX + side * FARROWING_CRATE_LAYOUT.gateAccessLaneRailFrom;
}

/**
 * ONE material for every pipe in the crate, built per `build()` call.
 *
 * It was constructed inside `pipe()` and therefore once per rail, which gave a
 * fifty-piece crate fifty identical materials and **106 draw calls for a single
 * object** — against a 400-call budget for the whole frame (spec §1), for a
 * crate the hall places sixteen of. `docs/asset-conventions.md` §3 asks for one
 * mesh with a few materials rather than twenty meshes; this is the same rule one
 * level down.
 *
 * Still per-call rather than module-level: factories are pure
 * (`docs/asset-conventions.md` §2) and a shared module-level material would be
 * disposed by whichever consumer finished first, taking every other instance in
 * the game down with it.
 */
function pipe(length: number, name: string, material: THREE.Material): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(PIPE_RADIUS, PIPE_RADIUS, length, 6, 1), material);
  mesh.name = name;
  mesh.castShadow = true;
  return mesh;
}

function railZ(group: THREE.Group, x: number, y: number, name: string, material: THREE.Material): void {
  railZSpan(group, x, y, -CRATE_LENGTH / 2, CRATE_LENGTH / 2, name, material);
}

function railZSpan(
  group: THREE.Group,
  x: number,
  y: number,
  from: number,
  to: number,
  name: string,
  material: THREE.Material,
): void {
  const rail = pipe(to - from, name, material);
  rail.rotation.x = Math.PI / 2;
  rail.position.set(x, y, (from + to) / 2);
  group.add(rail);
}

function railX(group: THREE.Group, z: number, y: number, length: number, name: string, material: THREE.Material, x = 0): void {
  const rail = pipe(length, name, material);
  rail.rotation.z = Math.PI / 2;
  rail.position.set(x, y, z);
  group.add(rail);
}

function upright(group: THREE.Group, x: number, z: number, height: number, name: string, material: THREE.Material): void {
  const post = pipe(height, name, material);
  post.position.set(x, height / 2, z);
  group.add(post);
}

function build(gateAccess = false): THREE.Group {
  const group = new THREE.Group();
  group.name = 'farrowing-crate';
  const deckMaterial = surface('metal', 0x87928d);
  const creepFloorMaterial = surface('metal', 0x7d8884);
  const pipeMaterial = surface('metal', 0xacb6b8);
  const footMaterial = surface('metal', 0x889295);

  // The raised strip is exactly the sow's 0.65 m lane. Keeping both creep floors
  // lower makes the three functional zones legible even in an overhead silhouette.
  const centralDeck = new THREE.Mesh(new THREE.BoxGeometry(0.65, 0.07, CRATE_LENGTH), deckMaterial);
  centralDeck.name = 'sow-deck';
  centralDeck.position.y = 0.035;
  centralDeck.receiveShadow = true;
  group.add(centralDeck);

  for (const x of [-0.61, 0.61]) {
    const creepFloor = new THREE.Mesh(new THREE.BoxGeometry(0.54, 0.035, CRATE_LENGTH), creepFloorMaterial);
    creepFloor.name = x < 0 ? 'creep-floor-left' : 'creep-floor-right';
    creepFloor.position.set(x, 0.0175, 0);
    creepFloor.receiveShadow = true;
    group.add(creepFloor);
  }

  // These two close-set, full-height partitions are the crate rather than a
  // perimeter pen. Their repeated rails and dense uprights deliberately make
  // the 0.65 m sow lane read as its own narrow cage at icon scale.
  const laneCage = new THREE.Group();
  laneCage.name = 'sow-lane-cage';
  group.add(laneCage);
  const laneRailFrom = gateAccess ? FARROWING_CRATE_LAYOUT.gateAccessLaneRailFrom : -CRATE_LENGTH / 2;
  // Five posts (0.5 m pitch) read as densely barred as seven at both preview
  // distance and 64 px; the asset's triangle count is what's actually visible
  // at the crate's 2200-triangle budget, so shaving redundant posts (and the
  // pipe radial segment count below, 8 -> 6) buys headroom without softening
  // the read that made the earlier all-uniform-height version fail review.
  for (const x of [-CAGE_HALF_WIDTH, CAGE_HALF_WIDTH]) {
    const side = x < 0 ? 'left' : 'right';
    for (const [index, y] of [0.18, 0.4, 0.62, 0.96].entries()) {
      railZSpan(laneCage, x, y, laneRailFrom, CRATE_LENGTH / 2, `lane-partition-${side}-bar-${index + 1}`, pipeMaterial);
    }
    for (const z of [-1.0, -0.5, 0, 0.5, 1.0]) {
      if (z < laneRailFrom) continue;
      upright(
        laneCage, x, z, FARROWING_CRATE_LAYOUT.outerEndRailTop,
        `lane-partition-${side}-post-${z.toFixed(1)}`, pipeMaterial,
      );
    }
  }

  // Creep bays are just low kerbed run-outs. Avoiding any waist-height outer
  // rail leaves their upper space unmistakably open beside the adult cage.
  const creepRails = new THREE.Group();
  creepRails.name = 'creep-area-kerbs';
  group.add(creepRails);
  for (const x of [-BAY_HALF_WIDTH, BAY_HALF_WIDTH]) {
    const side = x < 0 ? 'left' : 'right';
    railZ(creepRails, x, 0.18, `creep-${side}-kerb`, pipeMaterial);
    for (const z of [-1.0, 1.0]) upright(creepRails, x, z, 0.26, `creep-${side}-kerb-post-${z.toFixed(1)}`, pipeMaterial);
  }
  for (const z of [-1.0, 1.0]) {
    for (const [index, y] of [0.36, 0.96].entries()) {
      railX(laneCage, z, y, 0.65, `lane-end-${z.toFixed(1)}-bar-${index + 1}`, pipeMaterial);
    }
    for (const x of [-0.1625, 0.1625]) {
      upright(
        laneCage, x, z, FARROWING_CRATE_LAYOUT.outerEndRailTop,
        `lane-end-${z.toFixed(1)}-stave-${x.toFixed(4)}`, pipeMaterial,
      );
    }
    railX(creepRails, z, 0.18, 0.575, `creep-left-end-kerb-${z.toFixed(1)}`, pipeMaterial, -0.6125);
    railX(creepRails, z, 0.18, 0.575, `creep-right-end-kerb-${z.toFixed(1)}`, pipeMaterial, 0.6125);
  }

  // Feet intentionally land exactly on the grid. A former version began rails
  // at the deck and looked like it floated, especially in silhouette mode.
  for (const x of [-CAGE_HALF_WIDTH, CAGE_HALF_WIDTH, -BAY_HALF_WIDTH, BAY_HALF_WIDTH]) {
    for (const z of [-0.99, 0.99]) {
      const foot = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.02, 0.12), footMaterial);
      foot.name = `base-plate-${x.toFixed(3)}-${z.toFixed(1)}`;
      foot.position.set(x, 0.01, z);
      group.add(foot);
    }
  }
  return group;
}

export const farrowingCrateAsset: AssetDefinition = {
  id: 'farrowing-crate', label: 'Farrowing crate',
  description: 'Engineered sow cage with a 0.65 m central lane and protected creep areas on both sides.',
  footprint: { width: 1.8, height: 1.0, length: 2.1 }, create: build,
};

/** The one crate that meets Beat 2's gate mouth and therefore has a real doorway. */
export function farrowingCrateGateAccess(): THREE.Group {
  return build(true);
}
